import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import "@/components/desk/desk.css";
import { Light } from "@/components/desk/Light";
import { NextEvent } from "@/components/desk/NextEvent";
import { GoldPanel, GoldStats } from "@/components/desk/GoldPanel";
import { Headlines } from "@/components/desk/Headlines";
import { ConflictWatch } from "@/components/desk/ConflictWatch";
import { ConflictTimeline } from "@/components/desk/ConflictTimeline";
import { WeekAhead } from "@/components/desk/WeekAhead";
import { RulesCard } from "@/components/desk/RulesCard";
import { DeskControls } from "@/components/desk/DeskControls";
import { SourceBadges } from "@/components/desk/SourceBadges";
import { PinBar } from "@/components/desk/PinBar";

const useNoIndex = () => {
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    const prev = document.title;
    document.title = "Gold Desk";
    const man = document.querySelector('link[rel="manifest"]');
    const parent = man?.parentNode;
    man?.remove();
    return () => { m.remove(); if (man) parent.appendChild(man); document.title = prev; };
  }, []);
};

const KEY = "gtax_desk_token";

const useDeskToken = () => {
  const parts = useLocation().pathname.split("/").filter(Boolean);
  const [token, setToken] = useState(() => {
    if (parts[0] === "desk" && parts.length === 2) {
      localStorage.setItem(KEY, parts[1]);
      window.history.replaceState(null, "", "/desk");
      return parts[1];
    }
    return parts.length === 1 ? localStorage.getItem(KEY) : null;
  });
  return [token, setToken, parts[0] === "news"];
};

const NotFound = () => (
  <div className="desk desk-loading flex-col gap-2" data-testid="desk-not-found"><b>404</b><span>Page not found.</span></div>
);

const Login = ({ onToken }) => {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const go = (e) => {
    e.preventDefault();
    axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/desk-login`, { password: pw })
      .then((r) => { localStorage.setItem(KEY, r.data.token); onToken(r.data.token); })
      .catch((x) => setErr(x.response?.data?.detail || "Could not sign in"));
  };
  return (
    <form onSubmit={go} className="desk desk-loading flex-col gap-4" data-testid="desk-login">
      <b>Gold Desk</b>
      <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password" autoFocus
        className="desk-btn" style={{ width: 280, maxWidth: "90vw", textAlign: "center" }} data-testid="desk-login-password" />
      <button className="desk-btn" type="submit" data-testid="desk-login-submit">Open desk</button>
      {err && <span data-testid="desk-login-error">{err}</span>}
    </form>
  );
};

const More = ({ children }) => {
  const [open, setOpen] = useState(false);
  return (
    <section data-testid="desk-more">
      <button className="desk-btn w-full" onClick={() => setOpen(!open)} data-testid="desk-more-toggle">
        {open ? "Hide extra detail" : "Show extra detail (conflict watch, stats, rules, settings)"}
      </button>
      {open && <div className="desk-main !p-0 !mt-3 !max-w-none">{children}</div>}
    </section>
  );
};

export default function DeskPage() {
  useNoIndex();
  const [token, setToken, isNews] = useDeskToken();
  const [state, setState] = useState(null);
  const [failed, setFailed] = useState(false);
  const [missing, setMissing] = useState(!token);
  const api = `${process.env.REACT_APP_BACKEND_URL}/api/desk/${token}`;
  const load = useCallback(() => axios.get(`${api}/state`).then((r) => { setState(r.data); setFailed(false); })
    .catch((e) => {
      if (e.response?.status === 404) { localStorage.removeItem(KEY); setMissing(true); } else setFailed(true);
    }), [api]);
  useEffect(() => {
    if (!token || missing) return undefined;
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [token, missing, load]);
  if (missing && isNews) return <Login onToken={(t) => { setToken(t); setMissing(false); }} />;
  if (missing) return <NotFound />;
  if (!state) return (
    <div className="desk desk-loading flex-col gap-4" data-testid="desk-loading">
      {failed ? <><span data-testid="desk-load-error">Desk unreachable. Retrying every 30 s.</span><button className="desk-btn" onClick={load} data-testid="desk-retry">Retry now</button></> : "Loading desk…"}
    </div>
  );
  return (
    <div className="desk" data-testid="desk-page">
      <header className="desk-top">
        <span className="desk-brand">GOLD DESK</span>
        <SourceBadges sources={state.sources} />
      </header>
      <PinBar light={state.light} m={state.market} />
      {failed && <div className="sticky top-[45px] z-20 text-center text-xs py-2 bg-[#330d0f] text-[color:var(--red)]" data-testid="desk-stale">Connection lost, showing last data. Retrying…</div>}
      <main className="desk-main">
        <Light light={state.light} />
        <GoldPanel m={state.market} />
        <NextEvent ev={state.next_event} />
        <WeekAhead week={state.week} />
        <Headlines news={state.news} trump={state.trump} />
        <More>
          <ConflictWatch items={state.conflict} />
          <ConflictTimeline data={state.conflict_chart} />
          <GoldStats m={state.market} s={state.settings} />
          <RulesCard api={api} rules={state.rules} reload={load} />
          <DeskControls api={api} state={state} reload={load} />
        </More>
      </main>
      <footer className="desk-foot" data-testid="desk-footer">
        Internal risk tool, not investment advice. News can be late or wrong. Rules beat predictions.
        <div>Probability language only: tags describe likely pressure, never certainty.</div>
      </footer>
    </div>
  );
}
